<template>
  <!-- FI-1816 huddle panel, extracted from the erm list view so the weekly
       recap lives on the Today board. self-contained: loads its own
       schedule + counts per tenant and owns the preview modal and the
       send-now flow. design intent carried over from the 2026-06-05 polish:
       * at-a-glance counts replace the "click Preview to find out" flow
       * help tooltip carries the one-line "what is this?"
       * schedule line is the smaller sub-detail; counts are the lede -->
  <div v-if="tenant" class="hp-tile">
    <div class="hp-top">
      <div class="hp-title">
        <i class="fa fa-calendar-check-o"></i>&nbsp;Huddle
        <i class="fa fa-info-circle hp-info"
           v-b-tooltip.hover.right="helpTooltip"
           tabindex="0"
           aria-label="What is the Huddle?"></i>
      </div>
      <div class="hp-actions">
        <b-button
          v-if="canManage"
          size="sm"
          variant="outline-secondary"
          :disabled="previewLoading"
          title="Preview the exact email recipients will receive"
          @click="showPreview"
        >
          <i class="fa fa-eye"></i>&nbsp;Preview email
        </b-button>
        <b-button
          v-if="canManage"
          size="sm"
          variant="outline-secondary"
          :disabled="sending"
          title="Send this week's recap to your distribution list right now"
          @click="sendNow"
        >
          <i class="fa fa-paper-plane"></i>&nbsp;
          <span v-if="sending">Sending...</span>
          <span v-else>Send now</span>
        </b-button>
      </div>
    </div>

    <!-- this-week stat strip. zero-count buckets are hidden so the strip
         self-collapses on quiet weeks. -->
    <div class="hp-stats" v-if="statsLoading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading this week...
    </div>
    <div class="hp-stats" v-else-if="emptyWeek">
      <i class="fa fa-coffee hp-quiet-icon"></i>
      <span class="hp-quiet-text">Quiet week. Nothing open, nothing logged. Worth a nudge to the team that the form is up.</span>
    </div>
    <div class="hp-stats" v-else-if="counts">
      <span class="hp-stats-label">This week:</span>
      <span v-if="counts.new" class="hp-chip hp-chip--new">
        <span class="hp-chip-num">{{ counts.new }}</span> new
      </span>
      <span v-if="counts.in_progress" class="hp-chip hp-chip--inp">
        <span class="hp-chip-num">{{ counts.in_progress }}</span> in progress
      </span>
      <span v-if="counts.awaiting_followup" class="hp-chip hp-chip--wait">
        <span class="hp-chip-num">{{ counts.awaiting_followup }}</span> awaiting follow-up
      </span>
      <span v-if="counts.resolved_this_week" class="hp-chip hp-chip--done">
        <span class="hp-chip-num">{{ counts.resolved_this_week }}</span> resolved
      </span>
      <span v-if="counts.kudos_this_week" class="hp-chip hp-chip--kudos">
        <i class="fa fa-star"></i>&nbsp;<span class="hp-chip-num">{{ counts.kudos_this_week }}</span> kudos
      </span>
    </div>

    <!-- schedule + audience sub-line. small and muted; audience is the
         distribution list (FI-1816 reuse-distribution model). -->
    <div class="hp-sub">
      <span v-if="schedule && schedule.enabled" class="hp-sub-bit">
        <i class="fa fa-clock-o"></i>&nbsp;Sends {{ scheduleLabel }}<template v-if="canManage"> (<router-link class="hp-sub-link" :to="{ name: 'ops_desk_settings', query: { tenant: tenant, settings_tab: 'huddle' } }">manage</router-link>)</template>
      </span>
      <span v-else class="hp-sub-bit hp-sub-bit--muted">
        <i class="fa fa-pause-circle-o"></i>&nbsp;Sending paused<span v-if="scheduleLabel">&nbsp;(would run {{ scheduleLabel }})</span><template v-if="canManage"> (<router-link class="hp-sub-link" :to="{ name: 'ops_desk_settings', query: { tenant: tenant, settings_tab: 'huddle' } }">manage</router-link>)</template>
      </span>
      <span class="hp-sub-bit">
        <i class="fa fa-users"></i>&nbsp;Goes to the distribution list<template v-if="canManage"> (<router-link class="hp-sub-link" :to="{ name: 'ops_desk_settings', query: { tenant: tenant, settings_tab: 'distribution' } }">manage</router-link>)</template>
      </span>
    </div>

    <div v-if="sentSummary" class="hp-sent">
      <i class="fa fa-check-circle"></i>&nbsp;Sent to {{ sentSummary.recipients }} recipient<span v-if="sentSummary.recipients !== 1">s</span>
      <span class="hp-sent-meta">
        ({{ sentSummary.emailed }} emailed, {{ sentSummary.inserted }} in-app)
      </span>
    </div>

    <!-- FI-1816 preview modal. iframes the rendered email html (srcdoc)
         so the email styles don't bleed into the rest of the app and the
         preview matches what subscribers actually receive byte-for-byte. -->
    <b-modal
      id="huddle-preview-modal"
      title="Huddle preview"
      size="lg"
      ok-only
      ok-title="Close"
      ok-variant="outline-secondary"
      hide-header-close
    >
      <div v-if="previewLoading" class="hp-preview-loading">
        <i class="fa fa-spinner fa-spin"></i>&nbsp;Building preview...
      </div>
      <div v-else-if="previewError" class="hp-preview-error">
        {{ previewError }}
      </div>
      <iframe
        v-else
        :srcdoc="previewHtml"
        class="hp-preview-iframe"
        sandbox="allow-same-origin"
      ></iframe>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  get_ops_desk_huddle_schedule,
  get_ops_desk_huddle_preview_html,
  get_ops_desk_huddle_preview_json,
  send_ops_desk_huddle_now,
} from '../networking/apis'

export default {
  name: 'huddle_panel',
  props: {
    tenant: { type: String, default: '' },
  },
  data() {
    return {
      schedule: null,
      sending: false,
      sentSummary: null,
      previewLoading: false,
      previewHtml: '',
      previewError: '',
      statsLoading: false,
      counts: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // same gate as the erm settings entry point: tenant-side fleet admins
    // and staff admins can preview/send; everyone else just reads. tbn-admin
    // is the TBN-tenant fleet admin, same tier as customer-admin.
    canManage() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin' || role === 'tbn-admin'
    },
    // plain-english explainer rendered in a tooltip on the (i) glyph.
    // one-line on purpose: the tooltip is for a hover, not a dialog.
    helpTooltip() {
      return (
        'A recap of every ops event from the past week: open items, '
        + 'what came in, and kudos. Subscribers get it by email on the '
        + 'schedule you set.'
      )
    },
    emptyWeek() {
      const c = this.counts
      if (!c) return false
      const total = (c.total_open || 0) + (c.total_in_window || 0)
      return total === 0
    },
    // a friendly one-liner of the current huddle schedule. covers the
    // staples (sundays 11pm ET, weekdays 8am CT, etc.) and falls back
    // to the raw cron when the expression is exotic.
    scheduleLabel() {
      if (!this.schedule) return ''
      const cron = (this.schedule.cron || '').trim()
      const tz = this.schedule.tz || ''
      const tzShort = tz.split('/').pop().replace(/_/g, ' ')
      const parts = cron.split(/\s+/)
      if (parts.length < 5) return cron + ' ' + tzShort
      const [minute, hour, , , dow] = parts
      const dowMap = {
        SUN: 'Sundays', MON: 'Mondays', TUE: 'Tuesdays', WED: 'Wednesdays',
        THU: 'Thursdays', FRI: 'Fridays', SAT: 'Saturdays',
      }
      const dowLabel = dowMap[(dow || '').toUpperCase()] || ('day ' + dow)
      const minuteNum = parseInt(minute, 10) || 0
      const hourNum = parseInt(hour, 10)
      if (!Number.isFinite(hourNum)) return cron + ' ' + tzShort
      const ampm = hourNum >= 12 ? 'pm' : 'am'
      const display12 = ((hourNum + 11) % 12) + 1
      const minuteText = minuteNum === 0 ? '' : ':' + String(minuteNum).padStart(2, '0')
      return dowLabel + ' ' + display12 + minuteText + ampm + ' ' + tzShort
    },
  },

  watch: {
    // the navbar tenant switcher swaps ?tenant= without remounting, so the
    // board passes the new tenant down and we refetch for it.
    tenant() {
      this.sentSummary = null
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    // errors are intentionally soft (console warn + quiet state); the
    // panel degrades gracefully when the backend is unreachable so the
    // board isn't blocked.
    async load() {
      if (!this.tenant) return
      this.statsLoading = true
      // schedule is a cheap config read; the json preview is a one-tenant
      // gsi query bounded by the 7-day window so it's also fast.
      try {
        const [schRes, previewRes] = await Promise.all([
          get_ops_desk_huddle_schedule(this.tenant),
          get_ops_desk_huddle_preview_json(this.tenant).catch(() => null),
        ])
        this.schedule = (schRes && schRes.value && schRes.value.data) || null
        const preview = (previewRes && previewRes.value && previewRes.value.data) || null
        this.counts = (preview && preview.counts) || null
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('huddle_panel load failed', e)
      } finally {
        this.statsLoading = false
      }
    },

    async showPreview() {
      this.previewError = ''
      this.previewHtml = ''
      this.previewLoading = true
      // open the modal first so the user sees the loading state
      this.$bvModal.show('huddle-preview-modal')
      try {
        const res = await get_ops_desk_huddle_preview_html(this.tenant)
        // the backend wraps the rendered email in the standard
        // ApiSuccessResponse envelope, so the html string lives at
        // res.value.data.html (execute_generator already unwraps one
        // layer of `.data`).
        const data = res && res.value && res.value.data
        const html = data && (data.html || (typeof data === 'string' ? data : ''))
        if (typeof html === 'string' && html.length) {
          this.previewHtml = html
        } else {
          this.previewError = 'Empty preview response.'
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('huddle_panel showPreview failed', e)
        this.previewError = 'Could not load preview.'
      } finally {
        this.previewLoading = false
      }
    },

    async sendNow() {
      if (!this.tenant || this.sending) return
      // the audience is the distribution list (managed in Settings), so the
      // confirmation points there rather than guessing a subscriber count.
      const audience = 'Everyone on your distribution list gets a fresh email, and portal users also get an in-app notification.'
      const c = this.counts
      const recap = c
        ? 'It covers the last 7 days: '
          + [c.new && (c.new + ' new'), c.in_progress && (c.in_progress + ' in progress'),
             c.awaiting_followup && (c.awaiting_followup + ' awaiting follow-up'),
             c.resolved_this_week && (c.resolved_this_week + ' resolved'),
             c.kudos_this_week && (c.kudos_this_week + ' kudos')]
            .filter(Boolean).join(', ')
          + '.'
        : ''
      const message = ['Send this week\'s Huddle now?', audience, recap]
        .filter(Boolean).join(' ')
      const ok = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Send the Huddle now',
        okTitle: 'Send to distribution list',
        cancelTitle: 'Cancel',
        okVariant: 'primary',
      })
      if (!ok) return
      this.sending = true
      this.sentSummary = null
      try {
        const res = await send_ops_desk_huddle_now(this.tenant, false)
        const data = (res && res.value && res.value.data) || {}
        this.sentSummary = data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('huddle_panel sendNow failed', e)
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
/* tile chrome matches the ops board's .ob-tile so the panel slots into
   the today grid without a wrapper. */
.hp-tile {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #fbfcfe;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
.hp-top {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.hp-title {
  font-size: 12px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.4px;
  display: inline-flex; align-items: center;
}
.hp-info { color: #9ca3af; cursor: help; font-size: 12px; margin-left: 4px; }
.hp-actions { display: inline-flex; gap: 8px; flex-wrap: wrap; }

/* this-week stat strip: small count pills, color-keyed to status. */
.hp-stats {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  font-size: 12.5px; color: #4b5563;
}
.hp-stats-label { font-weight: 600; color: #6b7280; margin-right: 2px; }
.hp-quiet-icon { color: #b58100; margin-right: 4px; }
.hp-quiet-text { flex: 1 1 0; min-width: 0; }
.hp-chip {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 8px; border-radius: 999px;
  font-size: 12px; font-weight: 500;
  background: #eef2f6; border: 1px solid #dde4ec; color: #4b5563;
}
.hp-chip-num { font-weight: 700; }
.hp-chip--new   { background: #f3f4f6; border-color: #d1d5db; color: #374151; }
.hp-chip--inp   { background: #e7f1ff; border-color: #bcd8fb; color: #0b3d91; }
.hp-chip--wait  { background: #fff4d1; border-color: #f0d97a; color: #7a5400; }
.hp-chip--done  { background: #e7f8ed; border-color: #b5e3c5; color: #166534; }
.hp-chip--kudos { background: #fff4d1; border-color: #f0d97a; color: #7a5400; }

/* schedule + audience sub-line: muted, sits below the counts. */
.hp-sub {
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
  font-size: 12px; color: #6b7280;
}
.hp-sub-bit { display: inline-flex; align-items: center; }
.hp-sub-bit--muted { color: #9ca3af; }
.hp-sub-link { color: #1f5582; text-decoration: underline; }
.hp-sub-link:hover { color: var(--fi-rail-accent); }
.hp-sent {
  margin-top: 2px;
  font-size: 12.5px;
  color: #166534;
  background: #ecfdf5;
  border-radius: 4px;
  padding: 6px 10px;
}
.hp-preview-loading,
.hp-preview-error {
  padding: 16px;
  text-align: center;
  color: #6b7280;
}
.hp-preview-error { color: #b91c1c; }
.hp-preview-iframe {
  width: 100%;
  height: 60vh;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f5f7fa;
}
</style>
